import React, { useMemo } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";
import { AccountSearchCell } from "@components/spreadsheet/cells/AccountSearchCell";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { useAccounts } from "@hooks/useAccounts";
import { SuggestedLineCell } from "./SuggestedLineCell";

interface Props extends BaseCellProps<string> {
    entityId?: number;
    // When set, only accounts of these types appear in the picker. Used by the
    // Receivables column to match AccrualAccountCell /
    // AccountsReceivableCell (AR-only). The Accrual (revenue) column leaves
    // this unset — any active entity account is pickable.
    allowedAccountTypes?: AccountType[];
    // Role of the account JE generation falls back to while the line has none
    // chosen; that account is shown muted in the empty cell.
    defaultAccountRole?: AccountRole;
    onConfirmSuggestion?: () => void;
}

// Account cell for the native invoice drawer's line-items grid (PRO-10698).
// Scoped to the SELECTED entity only (never all workspace entities), and —
// unlike the importer's ImporterAccountSearchCell — never auto-writes a
// fallback account onto an untouched line: doing so here would create a
// phantom override row on lines the user never coded, since native invoice
// lines persist coding explicitly via `lineAccounting` rather than always
// carrying a resolved account. Leaving the cell blank until the user picks
// an account keeps "no coding" and "coded to the default account"
// distinguishable in the saved payload.
export const NativeInvoiceAccrualAccountCell: React.FC<Props> = ({
    entityId,
    allowedAccountTypes,
    defaultAccountRole,
    onConfirmSuggestion,
    row,
    value,
    onChange,
    ...props
}) => {
    const { accounts } = useAccounts({ entityId });
    const filteredAccounts = useMemo(() => {
        if (!allowedAccountTypes || allowedAccountTypes.length === 0) {
            return accounts;
        }
        return accounts.filter((account) =>
            allowedAccountTypes.some(
                (allowedType) => account.type === allowedType,
            ),
        );
    }, [accounts, allowedAccountTypes]);

    const placeholderAccount =
        defaultAccountRole == null
            ? undefined
            : accounts.find((account) => account.role === defaultAccountRole);

    const accountCell = (
        <AccountSearchCell
            {...props}
            row={row}
            value={value}
            onChange={onChange}
            accounts={filteredAccounts}
            placeholderAccount={placeholderAccount}
        />
    );
    const isSuggestedAccountShown = filteredAccounts.some(
        (account) => account.id === value,
    );
    if (!onConfirmSuggestion || !isSuggestedAccountShown) {
        return accountCell;
    }

    return (
        <SuggestedLineCell onConfirm={onConfirmSuggestion}>
            {accountCell}
        </SuggestedLineCell>
    );
};
